import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, money, fdt, fdate } from "../../api";
import { StatusPill, VEHICLE_LABELS } from "../../components/bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Download, Plus, Star, Trash2 } from "lucide-react";
import AddressField from "../../components/AddressField";

const inputCls = "cr-input !text-xs !py-2 !rounded-xl";
const btnBlack = "bg-[#1C1B1A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-all";

export function DeliveriesTab() {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.get("/jobs", { params: { status: status || undefined, page_size: 100 } })
      .then((r) => { setJobs(r.data.jobs); setLoading(false); })
      .catch(() => setLoading(false));
  }, [status]);
  useEffect(load, [load]);

  return (
    <div className="space-y-4" data-testid="deliveries-tab">
      <div className="inline-flex bg-[#EFECE2] p-1 rounded-full gap-1 flex-wrap">
        {["", "open", "assigned", "picked_up", "in_transit", "delivered", "cancelled"].map((s) => (
          <button key={s || "all"} data-testid={`del-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${status === s ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>
            {s ? s.replace(/_/g, " ") : "All"}
          </button>
        ))}
      </div>
      <div className="cr-card !p-0 cr-table-wrap">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
            <th className="p-3 font-semibold">Ref</th><th className="p-3 font-semibold">Route</th>
            <th className="p-3 font-semibold">Vehicle</th><th className="p-3 font-semibold">Pickup</th>
            <th className="p-3 font-semibold">Price (ex VAT)</th><th className="p-3 font-semibold">Status</th>
          </tr></thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-[#F6F3EA] last:border-0 hover:bg-[#FCFAF5] transition-colors" data-testid={`delivery-${j.ref}`}>
                <td className="p-3"><Link to={`/jobs/${j.id}`} className="font-mono font-semibold hover:underline">{j.ref}</Link></td>
                <td className="p-3">{j.pickup?.city} → {j.delivery?.city}</td>
                <td className="p-3">{VEHICLE_LABELS[j.vehicle_type] || "—"}</td>
                <td className="p-3 text-[#78756E]">{j.pickup_date ? fdate(j.pickup_date) : "Flexible"}</td>
                <td className="p-3 font-semibold">{j.price_net != null ? money(j.price_net, j.currency) : "Bids"}</td>
                <td className="p-3"><StatusPill status={j.status} /></td>
              </tr>
            ))}
            {!loading && jobs.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#A09D95]">No deliveries with this status yet.</td></tr>}
            {loading && <tr><td colSpan={6} className="p-10 text-center text-[#A09D95]">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ProvidersTab() {
  const [providers, setProviders] = useState(null);
  useEffect(() => { api.get("/company/providers").then((r) => setProviders(r.data.providers)).catch(() => setProviders([])); }, []);
  if (providers === null) return <p className="text-xs text-[#A09D95] py-10 text-center">Loading…</p>;

  return (
    <div className="cr-card !p-0 cr-table-wrap" data-testid="providers-tab">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
          <th className="p-3 font-semibold">Courier</th><th className="p-3 font-semibold">Vehicle</th>
          <th className="p-3 font-semibold">Jobs</th><th className="p-3 font-semibold">Delivered</th>
          <th className="p-3 font-semibold">Rating</th><th className="p-3 font-semibold">Last job</th>
        </tr></thead>
        <tbody>
          {providers.map((p) => (
            <tr key={p.courier_id} className="border-b border-[#F6F3EA] last:border-0" data-testid={`provider-${p.courier_id}`}>
              <td className="p-3">
                <p className="font-semibold">{p.name}</p>
                <p className="text-[10px] text-[#A09D95]">{p.country || ""}</p>
              </td>
              <td className="p-3">{VEHICLE_LABELS[p.vehicle_type] || "—"}</td>
              <td className="p-3 font-semibold">{p.jobs}</td>
              <td className="p-3">{p.delivered}</td>
              <td className="p-3">{p.rating_avg ? <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F2D06B] fill-[#F2D06B]" />{p.rating_avg.toFixed(1)}</span> : "—"}</td>
              <td className="p-3 text-[#78756E]">{p.last ? fdt(p.last) : "—"}</td>
            </tr>
          ))}
          {providers.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#A09D95]">No couriers yet — they appear here after working on your jobs.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function AddressBookTab() {
  const [addresses, setAddresses] = useState([]);
  const [label, setLabel] = useState("");
  const [point, setPoint] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/company/addresses").then((r) => setAddresses(r.data.addresses)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!point?.lat) return toast.error("Pick an address from the suggestions");
    setBusy(true);
    try {
      await api.post("/company/addresses", {
        label: label.trim() || point.city || "Address",
        address: point.address, city: point.city, postcode: point.postcode,
        country: point.country, lat: point.lat, lng: point.lng,
      });
      toast.success("Address saved");
      setLabel(""); setPoint(null); load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="address-book-tab">
      <p className="text-xs text-[#78756E]">Saved addresses appear in the booking wizard for one-click stops.</p>
      <form onSubmit={add} className="cr-card !p-4 grid sm:grid-cols-[180px_1fr_auto] gap-2 items-end">
        <div>
          <label className="cr-label mb-1.5 block">Label</label>
          <input data-testid="addr-label" placeholder="Main warehouse" value={label}
            onChange={(e) => setLabel(e.target.value)} className={`${inputCls} w-full`} />
        </div>
        <AddressField label="Address" testId="addr-new" value={point} onSelect={setPoint} />
        <button data-testid="addr-add-btn" disabled={busy} className={`${btnBlack} !py-2.5 disabled:opacity-40 whitespace-nowrap`}>
          {busy ? "Saving…" : "Save address"}
        </button>
      </form>
      <div className="cr-card !p-0">
        {addresses.map((a) => (
          <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[#F6F3EA] last:border-0" data-testid={`addr-${a.id}`}>
            <div className="min-w-0">
              <p className="text-xs font-semibold">{a.label}</p>
              <p className="text-[11px] text-[#A09D95] truncate">{a.address}</p>
            </div>
            <button data-testid={`addr-delete-${a.id}`} onClick={async () => {
              try { await api.delete(`/company/addresses/${a.id}`); toast.success("Removed"); load(); }
              catch (err) { toast.error(fmtErr(err)); }
            }} className="text-[#A09D95] hover:text-red-600 transition-colors shrink-0 ml-3"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {addresses.length === 0 && <p className="text-xs text-[#A09D95] text-center py-10">No saved addresses yet.</p>}
      </div>
    </div>
  );
}

export function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date_from: "", date_to: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/company/reports").then((r) => setReports(r.data.reports)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/company/reports", form);
      toast.success(`Report ready — ${data.rows} rows`);
      setOpen(false);
      load();
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/company/reports/${data.id}/csv`, "_blank");
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#78756E]">Export your delivery data as CSV for accounting.</p>
        <button data-testid="report-new-open" onClick={() => setOpen(true)} className={`${btnBlack} flex items-center gap-1.5`}>
          <Plus className="w-3.5 h-3.5" /> New report
        </button>
      </div>
      <div className="cr-card !p-0">
        {reports.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-4 py-3 border-b border-[#F6F3EA] last:border-0" data-testid={`report-${r.id}`}>
            <div>
              <p className="text-xs font-semibold">{r.date_from} → {r.date_to}</p>
              <p className="text-[11px] text-[#A09D95]">{r.rows} rows · by {r.created_by} · {fdt(r.created_at)}</p>
            </div>
            <a data-testid={`report-download-${r.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/company/reports/${r.id}/csv`} target="_blank" rel="noreferrer"
              className="cr-btn-outline !py-1.5 !px-3 !text-xs flex items-center gap-1.5">
              <Download className="w-3 h-3" /> CSV
            </a>
          </div>
        ))}
        {reports.length === 0 && <p className="text-xs text-[#A09D95] text-center py-10">No reports yet — generate your first one.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>New report</DialogTitle></DialogHeader>
          <form onSubmit={generate} className="space-y-2 mt-2">
            <label className="cr-label">From</label>
            <input data-testid="report-from" type="date" required value={form.date_from}
              onChange={(e) => setForm({ ...form, date_from: e.target.value })} className={`${inputCls} w-full`} />
            <label className="cr-label">To</label>
            <input data-testid="report-to" type="date" required value={form.date_to}
              onChange={(e) => setForm({ ...form, date_to: e.target.value })} className={`${inputCls} w-full`} />
            <button data-testid="report-generate-btn" disabled={busy} className={`${btnBlack} w-full !py-2.5 disabled:opacity-40`}>
              {busy ? "Generating…" : "Generate & download"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
